import { CommandBar, DetailsList, Panel, PanelType, Spinner, Selection, SelectionMode, MessageBar, MessageBarType } from "office-ui-fabric-react"
import { useEffect, useState } from "react";
import { createNewFloorplan, deleteFloorplan, loadFloorplansForLocation } from "../../../api/floorplans";
// import { LocationFormPanel } from "./LocationFormPanel";
import { useHistory } from "react-router-dom";
import { IFloorplan, IFloorplanAddResult } from "../../../types/SiteLocation.types"
import { FloorplanForm } from "../forms/FloorplanForm";
import { NewLayoutDialog } from "./NewLayoutDialog";

export interface IFloorplansPanelProps {
    onDismiss: () => void;
    locationId: number;
}

export const FloorplansPanel = (props: IFloorplansPanelProps) => {
    const history = useHistory();

    const [ formData, setFormData ] = useState<Partial<IFloorplan>>();
    const [ floorplans, setFloorplans ] = useState<Partial<IFloorplan>[]>();
    const [ error, setError ] = useState<string>();
    const [ loading, setLoading ] = useState(false);
    const [ floorplanSelected, setFloorplanSelected ] = useState(false);
    const [ showNewLayoutDialog, setShowNewLayoutDialog ] = useState<Partial<IFloorplan>>();

    const onSelectionChanged = () => {
        const selectedItems = selection.getSelection() as IFloorplan[];
        if (selectedItems !== undefined && selectedItems.length > 0) {
            setFloorplanSelected(true);
        } else {
            setFloorplanSelected(false);
        }
    };
    
    const [ selection ] = useState(new Selection({ onSelectionChanged }));

    const _loadFloorplans = async () => {
        setLoading(true);
        selection.setAllSelected(false);
        setFloorplanSelected(false);
        const locationsResponse = await loadFloorplansForLocation(props.locationId);
        if (locationsResponse.ok) {
            setFloorplans(await locationsResponse.json());
        } else {
            setError("Unable to load floorplans: " + locationsResponse.statusText);
        }
        setLoading(false);
    };

    const _newFloorplan = async () => {
        const response = await createNewFloorplan(props.locationId);
        if (response.ok) {
            const info = await response.json() as IFloorplanAddResult;
            history.push(`/admin/layoutEditor/floorplan/${info.floorplanId}/${info.layoutId}`);
        } else {
            setError("Unable to create new floorplan: " + response.statusText);
        }
    };

    const _editFloorplanLayout = async (floorplan: IFloorplan) => {
        if (floorplan.FloorplanLayout) {
            const layout = floorplan.FloorplanLayout;
            if (layout.Published) {
                setShowNewLayoutDialog(floorplan);
                return;
            } else {
                history.push(`/admin/layoutEditor/floorplan/${floorplan.Id}/${layout.Id}`);
            }
        }
    };

    const _deleteFloorplan = async (id: string) => {
        const deleteResponse = await deleteFloorplan(id);
        if (deleteResponse.ok) {
            _loadFloorplans();
        } else {
            setError("Unable to delete floorplan: " + deleteResponse.statusText)
        }
    }

    useEffect(() => {
        _loadFloorplans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Panel 
            headerText="Floorplans" 
            isOpen={true} 
            onDismiss={props.onDismiss}
            type={PanelType.medium} 
        >
            { error !== undefined && (
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            )}
            <p><strong>Maintain floorplans</strong></p>
            <p>
                You can use this menu to work on draft floorplans, and use the floorplan editor to publish them.
                Published floorplans define visual layouts and desks that users can book using the system.
            </p>
            <CommandBar
                items={[
                    {
                        key: "new",
                        text: "New",
                        iconProps: { iconName: "Add" },
                        onClick: () => {
                            _newFloorplan();
                        }
                    },
                    {
                        disabled: !floorplanSelected,
                        key: "rename",
                        text: "Rename",
                        iconProps: { iconName: "Rename" },
                        onClick: () => {
                            const selectedItems = selection.getSelection() as IFloorplan[];
                            if (selectedItems !== undefined && selectedItems.length > 0) {
                                const selectedItem = selectedItems[0];
                                setFormData(selectedItem as IFloorplan);
                            }
                        }
                    },
                    {
                        disabled: !floorplanSelected,
                        key: "edit",
                        text: "Edit Layout",
                        iconProps: { iconName: "Edit" },
                        onClick: () => {
                            const selectedItems = selection.getSelection() as IFloorplan[];
                            if (selectedItems !== undefined && selectedItems.length > 0) {
                                const selectedItem = selectedItems[0];
                                _editFloorplanLayout(selectedItem);
                            }
                        }
                    },
                    {
                        disabled: !floorplanSelected,
                        key: "delete",
                        text: "Delete",
                        iconProps: { iconName: "Delete" },
                        onClick: () => {
                            const selectedItems = selection.getSelection() as IFloorplan[];
                            if (selectedItems !== undefined && selectedItems.length > 0) {
                                const selectedItem = selectedItems[0];
                                if (window.confirm("Are you sure? Note: Please make sure there are no bookings active for this floor plan before deleting.")) {
                                    _deleteFloorplan(selectedItem.Id);
                                }
                            }
                        }
                    }
                ]}
            />
            { loading === true && (
                <Spinner />
            )}
            { loading === false && floorplans !== undefined && floorplans.length > 0 && (
                <DetailsList
                    items={floorplans}
                    columns={[
                        {
                            key: "name",
                            fieldName: "Name",
                            name: "Name",
                            minWidth: 40
                        },
                        {
                            key: "draft",
                            fieldName: "Name",
                            name: "Draft Indicator",
                            minWidth: 40,
                            isIconOnly: true,
                            onRender: (item: IFloorplan, index, column) => {
                                if (item.FloorplanLayout) {
                                    const layout = item.FloorplanLayout;
                                    if (!layout.Published) {
                                        return <div title="Layout is being edited">Draft</div>
                                    }
                                }
                            }
                        }
                    ]}
                    selection={selection}
                    selectionMode={SelectionMode.single}
                />
            )}
            { formData !== undefined && (
                <FloorplanForm floorplan={formData} onDismiss={(reloadFloorplans) => {
                    setFormData(undefined);
                    if (reloadFloorplans) {
                        _loadFloorplans();
                    }
                }} />
            )}
            { showNewLayoutDialog !== undefined && (
                <NewLayoutDialog floorplan={showNewLayoutDialog} onDismiss={() => setShowNewLayoutDialog(undefined)} onError={(err) => setError(err)} />
            )}
        </Panel>
    )
}